import { useEffect, useRef, useState } from 'react';

export const useLoadingBar = (callback, options) => {
  const timerRef = useRef(null);
  const completeTimerRef = useRef(null);
  const [status, setStatus] = useState('default');
  const [percent, setPercent] = useState(0);
  const { incrementCount = 5, incrementTick = 1000 } = options || {};

  const createPromise = (promise) => {
    const fallbackPromise = () =>
      new Promise((resolve) =>
        setTimeout(() => {
          return resolve(true);
        }, incrementCount * incrementTick)
      );

    return promise ? promise() : fallbackPromise();
  };

  const resetState = () => {
    setStatus('default');
    setPercent(0);
  };

  const resetTimer = () => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (completeTimerRef.current !== null) {
      clearInterval(completeTimerRef.current);
      completeTimerRef.current = null;
    }
  };

  const completeProcess = () => {
    setPercent(100);

    completeTimerRef.current = setTimeout(() => {
      setStatus('completed');
      callback && callback();
    }, 1000);
  };

  function startProcess(promise) {
    resetTimer();
    resetState();

    timerRef.current = setInterval(() => {
      setPercent((prev) => prev + 100 / incrementCount);
    }, incrementTick + 1);

    setStatus('loading');

    return createPromise(promise)
      .then((response) => response)
      .catch((error) => error)
      .finally(() => setStatus('cleanup'));
  }

  useEffect(() => {
    if (status === 'cleanup') {
      completeProcess();
    }
  }, [status, percent]);

  useEffect(() => {
    if (percent >= 100) {
      resetTimer();
    }
  }, [percent]);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null || (timerRef.current !== null && percent >= 100)) {
        resetTimer();
      }
    };
  }, []);

  return {
    percent,
    status,
    startProcess
  };
};
